import React, { useEffect, useMemo, useState } from "react";
import { clinicAdminScopeHeaders } from "../../../../utils/portalRoleChoice";
import "./OhtMessagesPage.css";

type ApiEnvelope<T> = {
  ok?: boolean;
  result?: T;
  error?: string;
};

type ClinicAdminOhtMessage = {
  ohtMessageId: number;
  ohtId: number | null;
  ohtName: string | null;
  ohtCode: string | null;

  messageType: string;
  title: string;
  body: string;
  targetScope: string;

  requiresAcknowledgement: boolean;
  expiresAt: string | null;
  isActive: boolean;

  createdAt: string | null;
  sentAt: string | null;
  createdByDisplayName: string | null;
  createdByEmail: string | null;

  clinicId: number | null;
  clinicName: string | null;

  acknowledged: boolean;
  acknowledgedAt: string | null;
  acknowledgedByDisplayName: string | null;
  acknowledgedByEmail: string | null;

  responseText: string | null;
  respondedAt: string | null;
};

type ClinicAdminOhtMessagesResult = {
  clinic?: {
    clinicId?: number;
    clinicName?: string;
    name?: string;
  };
  items?: ClinicAdminOhtMessage[];
};

type MessageFilter = "all" | "unread" | "requiresAck" | "acknowledged" | "expired";

type MessageTypeFilter =
  | "all"
  | "Announcement"
  | "PolicyUpdate"
  | "CapacityRequest"
  | "OperationalNotice";

async function parseJsonSafe(res: Response): Promise<any> {
  const text = await res.text();
  if (!text) return null;

  try {
    return JSON.parse(text);
  } catch {
    return { raw: text };
  }
}

async function requestResult<T>(path: string, init?: RequestInit): Promise<T> {
  const res = await fetch(path, {
    ...init,
    credentials: "include",
    headers: {
      "Content-Type": "application/json",
      ...(init?.headers || {}),
      ...clinicAdminScopeHeaders(),
    },
  });

  const body = await parseJsonSafe(res);

  if (!res.ok) {
    const msg =
      body && typeof body === "object" && body.error
        ? String(body.error)
        : `${res.status} ${res.statusText}`;
    throw new Error(msg);
  }

  if (body && typeof body === "object" && "ok" in body) {
    const envelope = body as ApiEnvelope<T>;
    if (envelope.ok !== true || envelope.result === undefined) {
      throw new Error(envelope.error || "Invalid API response.");
    }
    return envelope.result;
  }

  return body as T;
}

async function getClinicAdminOhtMessages(signal?: AbortSignal) {
  return requestResult<ClinicAdminOhtMessagesResult>(
    "/api/portal/clinic-admin/oht-messages",
    {
      method: "GET",
      signal,
    }
  );
}

async function acknowledgeClinicAdminOhtMessage(
  messageId: number,
  responseText: string | null
) {
  return requestResult<ClinicAdminOhtMessage>(
    `/api/portal/clinic-admin/oht-messages/${messageId}/acknowledge`,
    {
      method: "POST",
      body: JSON.stringify({
        responseText: responseText?.trim() || null,
      }),
    }
  );
}

function normalizeMessage(raw: any): ClinicAdminOhtMessage {
  return {
    ohtMessageId: Number(raw?.ohtMessageId || raw?.OHTMessageID || raw?.id || 0),
    ohtId: raw?.ohtId ?? raw?.OHTID ?? null,
    ohtName: raw?.ohtName ?? raw?.OHTName ?? null,
    ohtCode: raw?.ohtCode ?? raw?.OHTCode ?? null,

    messageType: raw?.messageType || raw?.MessageType || "Announcement",
    title: raw?.title || raw?.Title || "",
    body: raw?.body || raw?.Body || "",
    targetScope: raw?.targetScope || raw?.TargetScope || "",

    requiresAcknowledgement: !!(
      raw?.requiresAcknowledgement ?? raw?.RequiresAcknowledgement
    ),
    expiresAt: raw?.expiresAt ?? raw?.ExpiresAt ?? null,
    isActive: raw?.isActive ?? raw?.IsActive ?? true,

    createdAt: raw?.createdAt ?? raw?.CreatedAt ?? null,
    sentAt: raw?.sentAt ?? raw?.SentAt ?? raw?.createdAt ?? raw?.CreatedAt ?? null,
    createdByDisplayName:
      raw?.createdByDisplayName ?? raw?.CreatedByDisplayName ?? null,
    createdByEmail: raw?.createdByEmail ?? raw?.CreatedByEmail ?? null,

    clinicId: raw?.clinicId ?? raw?.ClinicID ?? null,
    clinicName: raw?.clinicName ?? raw?.ClinicName ?? null,

    acknowledged: !!(raw?.acknowledged ?? raw?.Acknowledged),
    acknowledgedAt: raw?.acknowledgedAt ?? raw?.AcknowledgedAt ?? null,
    acknowledgedByDisplayName:
      raw?.acknowledgedByDisplayName ?? raw?.AcknowledgedByDisplayName ?? null,
    acknowledgedByEmail:
      raw?.acknowledgedByEmail ?? raw?.AcknowledgedByEmail ?? null,

    responseText: raw?.responseText ?? raw?.ResponseText ?? null,
    respondedAt: raw?.respondedAt ?? raw?.RespondedAt ?? null,
  };
}

function typeLabel(type: string): string {
  switch (type) {
    case "Announcement":
      return "Announcement";
    case "PolicyUpdate":
      return "Policy update";
    case "CapacityRequest":
      return "Capacity request";
    case "OperationalNotice":
      return "Operational notice";
    default:
      return type || "Message";
  }
}

function typeClass(type: string): string {
  switch (type) {
    case "Announcement":
      return "announcement";
    case "PolicyUpdate":
      return "policy";
    case "CapacityRequest":
      return "capacity";
    case "OperationalNotice":
    default:
      return "operational";
  }
}

function scopeLabel(scope: string): string {
  switch (scope) {
    case "AllClinicsInOHT":
      return "All clinics in OHT";
    case "AcceptingClinicsOnly":
      return "Accepting clinics only";
    case "SelectedClinics":
      return "Selected clinics";
    default:
      return scope || "—";
  }
}

function fmtDateTime(value: string | null | undefined): string {
  if (!value) return "—";
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return value;

  return d.toLocaleString("en-CA", {
    year: "numeric",
    month: "short",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
  });
}

function isExpired(value: string | null | undefined): boolean {
  if (!value) return false;
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return false;
  return d.getTime() < Date.now();
}

function truncate(text: string, max = 170): string {
  const clean = (text || "").trim();
  if (clean.length <= max) return clean;
  return `${clean.slice(0, max - 1)}…`;
}

function StatusPill({
  label,
  tone = "neutral",
}: {
  label: string;
  tone?: "good" | "warn" | "danger" | "neutral";
}) {
  return (
    <span className={`clinic-oht-status-pill clinic-oht-status-pill--${tone}`}>
      {label}
    </span>
  );
}

export default function OhtMessagesPage() {
  const [clinicName, setClinicName] = useState<string>("");
  const [messages, setMessages] = useState<ClinicAdminOhtMessage[]>([]);
  const [selectedId, setSelectedId] = useState<number | null>(null);

  const [filter, setFilter] = useState<MessageFilter>("all");
  const [typeFilter, setTypeFilter] = useState<MessageTypeFilter>("all");
  const [search, setSearch] = useState("");

  const [responseText, setResponseText] = useState("");
  const [loading, setLoading] = useState(true);
  const [savingAck, setSavingAck] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  async function load(signal?: AbortSignal) {
    setLoading(true);
    setError(null);

    try {
      const result = await getClinicAdminOhtMessages(signal);
      const normalized = (result.items || []).map(normalizeMessage);
      setClinicName(result.clinic?.clinicName || result.clinic?.name || "");
      setMessages(normalized);
    } catch (err: any) {
      if (err?.name === "AbortError") return;
      setError(String(err?.message || "Failed to load OHT messages."));
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    const controller = new AbortController();
    load(controller.signal);

    return () => {
      controller.abort();
    };
  }, []);

  const selected = useMemo(() => {
    return messages.find((m) => m.ohtMessageId === selectedId) || null;
  }, [messages, selectedId]);

  useEffect(() => {
    setResponseText(selected?.responseText || "");
  }, [selectedId, selected?.responseText]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();

    return messages
      .filter((message) => {
        if (typeFilter !== "all" && message.messageType !== typeFilter) {
          return false;
        }

        if (filter === "unread" && message.acknowledged) return false;
        if (filter === "requiresAck" && !message.requiresAcknowledgement) return false;
        if (filter === "acknowledged" && !message.acknowledged) return false;
        if (filter === "expired" && !isExpired(message.expiresAt)) return false;

        if (!q) return true;

        return (
          message.title.toLowerCase().includes(q) ||
          message.body.toLowerCase().includes(q) ||
          typeLabel(message.messageType).toLowerCase().includes(q) ||
          (message.ohtName || "").toLowerCase().includes(q)
        );
      })
      .sort((a, b) => {
        const aTime = new Date(a.sentAt || a.createdAt || 0).getTime();
        const bTime = new Date(b.sentAt || b.createdAt || 0).getTime();
        return bTime - aTime;
      });
  }, [messages, filter, typeFilter, search]);

  const totals = useMemo(() => {
    return messages.reduce(
      (acc, message) => {
        acc.total += 1;
        if (!message.acknowledged) acc.unread += 1;
        if (message.requiresAcknowledgement) acc.requiresAck += 1;
        if (message.acknowledged) acc.acknowledged += 1;
        if (message.responseText) acc.responses += 1;
        if (isExpired(message.expiresAt)) acc.expired += 1;
        return acc;
      },
      {
        total: 0,
        unread: 0,
        requiresAck: 0,
        acknowledged: 0,
        responses: 0,
        expired: 0,
      }
    );
  }, [messages]);

  async function handleAcknowledge() {
    if (!selected) return;

    setSavingAck(true);
    setError(null);
    setSuccess(null);

    try {
      const updated = await acknowledgeClinicAdminOhtMessage(
        selected.ohtMessageId,
        responseText
      );

      const normalized = normalizeMessage(updated);

      setMessages((prev) =>
        prev.map((message) =>
          message.ohtMessageId === selected.ohtMessageId
            ? { ...message, ...normalized, acknowledged: true }
            : message
        )
      );

      setSuccess("Message acknowledged.");
    } catch (err: any) {
      setError(String(err?.message || "Failed to acknowledge message."));
    } finally {
      setSavingAck(false);
    }
  }

  return (
    <div className="clinic-oht-messages-page">
      <header className="clinic-oht-messages-header">
        <h1>OHT Messages</h1>
        <p>
          Announcements, capacity requests, policy updates, and operational notices from your Ontario Health Team.
          {clinicName ? ` These messages are shown for ${clinicName}.` : ""}
        </p>
      </header>

      {error ? (
        <div className="clinic-oht-alert clinic-oht-alert--error">{error}</div>
      ) : null}

      {success ? (
        <div className="clinic-oht-alert clinic-oht-alert--success">{success}</div>
      ) : null}

      <section className="clinic-oht-kpi-grid">
        <KpiCard label="Messages" value={totals.total} hint="Total messages received" />
        <KpiCard label="Unread" value={totals.unread} hint="Not yet acknowledged" />
        <KpiCard
          label="Requires acknowledgement"
          value={totals.requiresAck}
          hint={`${totals.acknowledged} acknowledged`}
        />
        <KpiCard
          label="Responses sent"
          value={totals.responses}
          hint={`${totals.expired} expired messages`}
        />
      </section>

      <section className="clinic-oht-card">
        <h2>Filters</h2>
        <div className="clinic-oht-filters-grid">
          <div>
            <label className="clinic-oht-label" htmlFor="oht-message-search">
              Search
            </label>
            <input
              id="oht-message-search"
              className="clinic-oht-input"
              type="search"
              value={search}
              placeholder="Search title, body, OHT, or type"
              onChange={(e) => setSearch(e.target.value)}
            />
          </div>

          <div>
            <label className="clinic-oht-label" htmlFor="oht-message-filter">
              Status
            </label>
            <select
              id="oht-message-filter"
              className="clinic-oht-input"
              value={filter}
              onChange={(e) => setFilter(e.target.value as MessageFilter)}
            >
              <option value="all">All messages</option>
              <option value="unread">Unread</option>
              <option value="requiresAck">Requires acknowledgement</option>
              <option value="acknowledged">Acknowledged</option>
              <option value="expired">Expired</option>
            </select>
          </div>

          <div>
            <label className="clinic-oht-label" htmlFor="oht-message-type">
              Type
            </label>
            <select
              id="oht-message-type"
              className="clinic-oht-input"
              value={typeFilter}
              onChange={(e) => setTypeFilter(e.target.value as MessageTypeFilter)}
            >
              <option value="all">All types</option>
              <option value="Announcement">Announcement</option>
              <option value="PolicyUpdate">Policy update</option>
              <option value="CapacityRequest">Capacity request</option>
              <option value="OperationalNotice">Operational notice</option>
            </select>
          </div>
        </div>
      </section>

      <section className="clinic-oht-workspace">
        <div className="clinic-oht-inbox">
          <div className="clinic-oht-inbox-header">
            <h2>Inbox {loading ? "" : `(${filtered.length})`}</h2>
            <button
              type="button"
              onClick={() => load()}
              className="clinic-oht-refresh-button"
            >
              Refresh
            </button>
          </div>

          {loading ? (
            <div className="clinic-oht-empty">Loading OHT messages…</div>
          ) : filtered.length === 0 ? (
            <div className="clinic-oht-empty">
              No OHT messages match your filters.
            </div>
          ) : (
            <div className="clinic-oht-message-list">
              {filtered.map((message) => {
                const active = message.ohtMessageId === selectedId;
                const expired = isExpired(message.expiresAt);

                return (
                  <button
                    key={message.ohtMessageId}
                    type="button"
                    onClick={() => {
                      setSelectedId(message.ohtMessageId);
                      setSuccess(null);
                      setError(null);
                    }}
                    className={`clinic-oht-message-row${
                      active ? " clinic-oht-message-row--active" : ""
                    }`}
                  >
                    <div className="clinic-oht-message-row-top">
                      <span
                        className={`clinic-oht-type-pill clinic-oht-type-pill--${typeClass(
                          message.messageType
                        )}`}
                      >
                        {typeLabel(message.messageType)}
                      </span>

                      <div className="clinic-oht-message-row-badges">
                        {message.requiresAcknowledgement ? (
                          message.acknowledged ? (
                            <StatusPill label="Acknowledged" tone="good" />
                          ) : (
                            <StatusPill label="Action needed" tone="warn" />
                          )
                        ) : null}
                        {expired ? <StatusPill label="Expired" tone="danger" /> : null}
                      </div>
                    </div>

                    <div className="clinic-oht-message-title">
                      {message.title || "Untitled message"}
                    </div>

                    <div className="clinic-oht-message-preview">
                      {truncate(message.body)}
                    </div>

                    <div className="clinic-oht-message-meta">
                      {message.ohtName || "OHT"} ·{" "}
                      {fmtDateTime(message.sentAt || message.createdAt)}
                    </div>
                  </button>
                );
              })}
            </div>
          )}
        </div>

        <div className="clinic-oht-detail">
          {!selected ? (
            <div className="clinic-oht-detail-empty">
              <div>
                <h2>Select a message</h2>
                <p>
                  Open a message to read the full content and acknowledge it when required.
                </p>
              </div>
            </div>
          ) : (
            <article className="clinic-oht-article">
              <header className="clinic-oht-article-header">
                <div className="clinic-oht-badge-row">
                  <span
                    className={`clinic-oht-type-pill clinic-oht-type-pill--${typeClass(
                      selected.messageType
                    )}`}
                  >
                    {typeLabel(selected.messageType)}
                  </span>

                  <StatusPill label={scopeLabel(selected.targetScope)} />

                  {selected.requiresAcknowledgement ? (
                    selected.acknowledged ? (
                      <StatusPill label="Acknowledged" tone="good" />
                    ) : (
                      <StatusPill label="Acknowledgement required" tone="warn" />
                    )
                  ) : (
                    <StatusPill label="Acknowledgement not required" />
                  )}

                  {isExpired(selected.expiresAt) ? (
                    <StatusPill label="Expired" tone="danger" />
                  ) : null}
                </div>

                <h2 className="clinic-oht-article-title">
                  {selected.title || "Untitled message"}
                </h2>

                <div className="clinic-oht-article-meta">
                  From {selected.ohtName || "OHT"}{" "}
                  {selected.createdByDisplayName || selected.createdByEmail
                    ? `· ${selected.createdByDisplayName || selected.createdByEmail}`
                    : ""}
                  <br />
                  Sent {fmtDateTime(selected.sentAt || selected.createdAt)}
                  {selected.expiresAt ? ` · Expires ${fmtDateTime(selected.expiresAt)}` : ""}
                </div>
              </header>

              <div className="clinic-oht-body-box">
                {selected.body || "—"}
              </div>

              <section className="clinic-oht-ack-section">
                <h3>Clinic acknowledgement</h3>

                {selected.acknowledged ? (
                  <div className="clinic-oht-ack-box clinic-oht-ack-box--success">
                    Acknowledged {fmtDateTime(selected.acknowledgedAt)}
                    {selected.acknowledgedByDisplayName || selected.acknowledgedByEmail
                      ? ` by ${selected.acknowledgedByDisplayName || selected.acknowledgedByEmail}`
                      : ""}
                  </div>
                ) : selected.requiresAcknowledgement ? (
                  <div className="clinic-oht-ack-box clinic-oht-ack-box--warning">
                    This message requires acknowledgement from your clinic.
                  </div>
                ) : (
                  <div className="clinic-oht-ack-box clinic-oht-ack-box--neutral">
                    Acknowledgement is optional for this message.
                  </div>
                )}

                <label className="clinic-oht-label" htmlFor="oht-message-response">
                  Optional response to OHT
                </label>
                <textarea
                  id="oht-message-response"
                  className="clinic-oht-input clinic-oht-textarea"
                  rows={4}
                  value={responseText}
                  onChange={(e) => setResponseText(e.target.value)}
                  placeholder="Optional note or response..."
                />

                <button
                  type="button"
                  onClick={handleAcknowledge}
                  disabled={savingAck || (isExpired(selected.expiresAt) && !selected.acknowledged)}
                  className="clinic-oht-primary-button"
                >
                  {savingAck
                    ? "Saving…"
                    : selected.acknowledged
                    ? "Update response"
                    : "Acknowledge message"}
                </button>

                {isExpired(selected.expiresAt) && !selected.acknowledged ? (
                  <div className="clinic-oht-expired-note">
                    This message has expired and can no longer be acknowledged.
                  </div>
                ) : null}
              </section>
            </article>
          )}
        </div>
      </section>
    </div>
  );
}

function KpiCard({
  label,
  value,
  hint,
}: {
  label: string;
  value: number;
  hint: string;
}) {
  return (
    <div className="clinic-oht-kpi-card">
      <div className="clinic-oht-kpi-label">{label}</div>
      <div className="clinic-oht-kpi-value">{value.toLocaleString()}</div>
      <div className="clinic-oht-kpi-hint">{hint}</div>
    </div>
  );
}